import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiInput from "components/SuiInput";
import GoalButton from "layouts/goals/components/GoalButton";
import { changePasswordReq } from "services/api";
import { updateToken } from "layouts/authentication/auth.reducer";
import { NO_AUTOFILL_PROPS } from "./passwordInputProps";

const MIN_PASSWORD_LENGTH = 8;

function FieldLabel({ children }) {
  return (
    <SuiTypography variant="caption" color="text" fontWeight="medium" display="block" mb={0.5}>
      {children}
    </SuiTypography>
  );
}

const parseErrorMessage = (err) => {
  const data = err?.response?.data;
  if (typeof data === "string" && data) return data;
  return data?.errors?.[0]?.msg || "Failed to change password.";
};

// Lets any signed-in admin change their own password. The API invalidates every
// older token on success and returns a fresh one, which we store so this
// session carries on.
function ChangePasswordForm() {
  const adminId = useSelector((state) => state.auth.admin?.id);
  const dispatch = useDispatch();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async () => {
    setSuccess(false);
    if (!currentPassword) return setError("Enter your current password.");
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      return setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
    }
    if (newPassword !== confirmPassword) return setError("New passwords do not match.");

    setIsSubmitting(true);
    setError(null);
    try {
      const { data } = await changePasswordReq({
        admin_id: adminId,
        body: { current_password: currentPassword, new_password: newPassword },
      });
      dispatch(updateToken(data.token));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setSuccess(true);
    } catch (err) {
      setError(parseErrorMessage(err));
    }
    setIsSubmitting(false);
  };

  return (
    <SuiBox maxWidth="420px">
      {error && (
        <SuiTypography variant="caption" color="error" display="block" mb={1}>
          {error}
        </SuiTypography>
      )}
      {success && (
        <SuiTypography variant="caption" color="success" fontWeight="medium" display="block" mb={1}>
          Password changed. You&apos;ve been signed out of your other sessions.
        </SuiTypography>
      )}
      <SuiBox mb={2}>
        <FieldLabel>Current password</FieldLabel>
        <SuiInput
          name="profile-current-password"
          type="password"
          {...NO_AUTOFILL_PROPS}
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
        />
      </SuiBox>
      <SuiBox mb={2}>
        <FieldLabel>New password</FieldLabel>
        <SuiInput
          name="profile-new-password"
          type="password"
          placeholder="At least 8 characters"
          {...NO_AUTOFILL_PROPS}
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
        />
      </SuiBox>
      <SuiBox mb={3}>
        <FieldLabel>Confirm new password</FieldLabel>
        <SuiInput
          name="profile-confirm-password"
          type="password"
          {...NO_AUTOFILL_PROPS}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
      </SuiBox>
      <GoalButton icon="lock_reset" loading={isSubmitting} onClick={handleSave}>
        Change password
      </GoalButton>
    </SuiBox>
  );
}

export default ChangePasswordForm;
